import { useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import {
    Bar,
    BarChart,
    CartesianGrid,
    Legend,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
    LabelList,
} from 'recharts';
import SalesLayout from '../../components/SalesLayout';
import { PageHero, HeroButton, HeroIcons } from '../../components/SalesUI';
import PageLoader from '../../components/PageLoader';
import axiosInstance from '../../utils/axiosInstance';

const DEFAULT_SUMMARY = {
    totalInquiries: 0,
    registered: 0,
    pipeline: 0,
    conversionRate: 0,
    monthOverMonth: {
        direction: 'flat',
        value: 0,
        label: 'previous period',
    },
    checklistCompletionRate: 0,
};

const DEFAULT_PIPELINE_SNAPSHOT = {
    pending: 0,
    followup: 0,
    planToRegister: 0,
    stalled: 0,
    lost: 0,
};

const DEFAULT_TEAM_HIGHLIGHTS = {
    topClosers: [],
    followupChampions: [],
    checklistChampions: [],
};

const DEFAULT_FOLLOWUP_ATTENTION = {
    missing: { count: 0, inquiries: [] },
    overdue: { count: 0, inquiries: [] },
};

const DEFAULT_COURSE_PERFORMANCE_SUMMARY = {
    strong: null,
    weak: null,
};

const resolveTrendColor = (trend) => {
    switch (trend) {
    case 'up':
        return 'bg-emerald-100 text-emerald-700 ring-emerald-200';
    case 'down':
        return 'bg-rose-100 text-rose-700 ring-rose-200';
    default:
        return 'bg-slate-100 text-slate-600 ring-slate-200';
    }
};

const resolveTrendIcon = (trend) => {
    if (trend === 'up') {
        return '▲';
    }

    if (trend === 'down') {
        return '▼';
    }

    return '◆';
};

const formatDelta = (value) => (value > 0 ? `+${value}` : value.toString());

const formatIndianNumber = (value) => new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(value);

function resolveFinancialYearInfo() {
    const today = new Date();
    const year = today.getFullYear();
    const monthIndex = today.getMonth(); // 0 = Jan, 3 = Apr

    const startYear = monthIndex >= 3 ? year : year - 1;
    const endYear = startYear + 1;
    const shortLabel = `FY ${startYear}-${String(endYear).slice(-2)}`;
    const periodLabel = `Apr ${startYear} to Mar ${endYear}`;

    return { startYear, endYear, shortLabel, periodLabel };
}

function isMonthKeyInFinancialYear(monthKey, fy) {
    // monthKey is expected as 'YYYY-MM'
    const [rawYear, rawMonth] = monthKey.split('-');
    const y = Number.parseInt(rawYear ?? '', 10);
    const m = Number.parseInt(rawMonth ?? '', 10);
    if (!Number.isFinite(y) || !Number.isFinite(m)) {
        return false;
    }

    // FY spans Apr (4) of startYear to Mar (3) of endYear
    if (y === fy.startYear) {
        return m >= 4 && m <= 12;
    }
    if (y === fy.endYear) {
        return m >= 1 && m <= 3;
    }
    return false;
}

function buildFinancialYearMonths(fy) {
    const months = [];
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    // Apr (4) to Dec (12) of startYear
    for (let m = 4; m <= 12; m += 1) {
        const monthKey = `${fy.startYear}-${String(m).padStart(2, '0')}`;
        const label = `${monthNames[m - 1]} ${fy.startYear}`;
        months.push({ monthKey, label });
    }

    // Jan (1) to Mar (3) of endYear
    for (let m = 1; m <= 3; m += 1) {
        const monthKey = `${fy.endYear}-${String(m).padStart(2, '0')}`;
        const label = `${monthNames[m - 1]} ${fy.endYear}`;
        months.push({ monthKey, label });
    }

    return months;
}

const Performance = ({ pageTitle, section }) => {
    const [summary, setSummary] = useState(DEFAULT_SUMMARY);
    const [monthlyRegistrations, setMonthlyRegistrations] = useState([]);
    const [courseOptions, setCourseOptions] = useState([]);
    const [courseMonthlyTrends, setCourseMonthlyTrends] = useState([]);
    const [teamHighlights, setTeamHighlights] = useState(DEFAULT_TEAM_HIGHLIGHTS);
    const [improvementAreas, setImprovementAreas] = useState([]);
    const [pipelineSnapshot, setPipelineSnapshot] = useState(DEFAULT_PIPELINE_SNAPSHOT);
    const [checklistGaps, setChecklistGaps] = useState([]);
    const [loggedStaffId, setLoggedStaffId] = useState(null);
    const [statusSummary, setStatusSummary] = useState([]);
    const [coursePerformanceSummary, setCoursePerformanceSummary] = useState(DEFAULT_COURSE_PERFORMANCE_SUMMARY);
    const [followupAttention, setFollowupAttention] = useState(DEFAULT_FOLLOWUP_ATTENTION);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [selectedCourseId, setSelectedCourseId] = useState(null);
    const [activeFollowupKey, setActiveFollowupKey] = useState(null);
    const [activeChecklistGap, setActiveChecklistGap] = useState(null);
    const financialYearInfo = useMemo(() => resolveFinancialYearInfo(), []);

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            try {
                setLoading(true);
                setError(null);
                const resp = await axiosInstance.get('/admin/sales-performance');
                if (cancelled || !resp?.data) return;
                const data = resp.data || {};

                setSummary(data.summary || DEFAULT_SUMMARY);
                setMonthlyRegistrations(Array.isArray(data.monthlyRegistrations) ? data.monthlyRegistrations : []);
                const incomingCourseOptions = Array.isArray(data.courseOptions) ? data.courseOptions : [];
                setCourseOptions(incomingCourseOptions);
                setCourseMonthlyTrends(Array.isArray(data.courseMonthlyTrends) ? data.courseMonthlyTrends : []);
                setTeamHighlights(data.teamHighlights || DEFAULT_TEAM_HIGHLIGHTS);
                setImprovementAreas(Array.isArray(data.improvementAreas) ? data.improvementAreas : []);
                setPipelineSnapshot(data.pipelineSnapshot || DEFAULT_PIPELINE_SNAPSHOT);
                setChecklistGaps(Array.isArray(data.checklistGaps) ? data.checklistGaps : []);
                setLoggedStaffId(data.loggedStaffId ?? null);
                setStatusSummary(Array.isArray(data.statusSummary) ? data.statusSummary : []);
                setCoursePerformanceSummary(data.coursePerformanceSummary || DEFAULT_COURSE_PERFORMANCE_SUMMARY);
                setFollowupAttention(data.followupAttention || DEFAULT_FOLLOWUP_ATTENTION);

                if (incomingCourseOptions.length && selectedCourseId === null) {
                    setSelectedCourseId(incomingCourseOptions[0].id ?? null);
                }
            } catch (err) {
                if (!cancelled) {
                    setError('Failed to load performance data.');
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        load();

        return () => {
            cancelled = true;
        };
    }, [selectedCourseId]);

    useEffect(() => {
        if (!courseOptions.length) {
            setSelectedCourseId(null);
            return;
        }

        setSelectedCourseId((previous) => {
            if (previous === null) {
                return courseOptions[0]?.id ?? null;
            }

            const exists = courseOptions.some((option) => option.id === previous);
            return exists ? previous : (courseOptions[0]?.id ?? null);
        });
    }, [courseOptions]);

    const openFollowupModal = (key) => {
        const group = followupAttention[key];
        if (!group || group.count === 0) {
            return;
        }
        setActiveFollowupKey(key);
    };

    const closeFollowupModal = () => setActiveFollowupKey(null);

    const activeFollowupGroup = activeFollowupKey ? followupAttention[activeFollowupKey] : null;
    const closeChecklistModal = () => setActiveChecklistGap(null);

    return (
        <SalesLayout title={pageTitle} currentSection={section}>
            {loading ? (
                <PageLoader label="Loading performance insights…" />
            ) : (
            <div className="sales-stagger space-y-6">
                <PageHero
                    icon={HeroIcons.performance}
                    title={pageTitle || 'Performance'}
                    subtitle="Celebrate every closed registration, share quick wins with the squad, and focus today's energy on the highest-impact actions."
                    gradient="bg-gradient-to-br from-emerald-600 via-teal-600 to-cyan-600"
                    chips={[financialYearInfo ? `${financialYearInfo.shortLabel} · ${financialYearInfo.periodLabel}` : null]}
                />
                {error && (
                    <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                        {error}
                    </div>
                )}
                <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                    <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
                        <div className="max-w-2xl space-y-3">
                            <h2 className="text-xl font-semibold text-slate-900">Your numbers at a glance</h2>
                            <p className="text-sm text-slate-500">
                                Celebrate every closed registration, share quick wins with the squad, and focus today&apos;s energy on the highest-impact actions.
                            </p>
                        </div>
                        <div className="grid w-full gap-4 sm:grid-cols-2 lg:w-auto lg:grid-cols-4">
                            <SummaryCard label="Total inquiries" value={summary.totalInquiries} tone="neutral" />
                            <SummaryCard label="Registered" value={summary.registered} tone="success" />
                            <SummaryCard label="In pipeline" value={summary.pipeline} tone="warning" />
                            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                                <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Conversion rate</p>
                                <p className="mt-2 text-xl font-semibold text-slate-900">{summary.conversionRate}%</p>
                                <div className="mt-3 flex items-center gap-2 text-xs text-slate-500">
                                    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-[10px] font-semibold ring-1 ${resolveTrendColor(summary.monthOverMonth.direction)}`}>
                                        <span>{resolveTrendIcon(summary.monthOverMonth.direction)}</span>
                                        <span>{formatDelta(summary.monthOverMonth.value)}</span>
                                    </span>
                                    <span>
                                        vs {summary.monthOverMonth.label}
                                    </span>
                                </div>
                                <p className="mt-3 text-[11px] text-slate-500">
                                    {summary.checklistCompletionRate}% of registrations close with a full checklist.
                                </p>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <div>
                            <h2 className="text-lg font-semibold text-slate-900">Month-wise registrations</h2>
                            <p className="text-sm text-slate-500">Track how actual registration revenue stacks up against the monthly targets set for the team.</p>
                        </div>
                        <span className="text-xs font-medium uppercase tracking-wide text-slate-400">
                            {financialYearInfo
                                ? `Financial year ${financialYearInfo.shortLabel} (${financialYearInfo.periodLabel})`
                                : `Last ${monthlyRegistrations.length} months`}
                        </span>
                    </div>
                    <MonthlyRegistrationChart data={monthlyRegistrations} financialYearInfo={financialYearInfo} />
                </section>

                <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <div>
                            <h2 className="text-lg font-semibold text-slate-900">Course-wise performance</h2>
                            <p className="text-sm text-slate-500">Pick a course to understand how its registrations trend month to month.</p>
                        </div>
                        <span className="text-xs font-medium uppercase tracking-wide text-slate-400">
                            {financialYearInfo
                                ? `Financial year ${financialYearInfo.shortLabel} (${financialYearInfo.periodLabel})`
                                : `Last ${monthlyRegistrations.length} months`}
                        </span>
                    </div>
                    <CoursePerformanceSection
                        options={courseOptions}
                        trends={courseMonthlyTrends}
                        selectedCourseId={selectedCourseId}
                        onSelect={setSelectedCourseId}
                        summary={coursePerformanceSummary}
                        financialYearInfo={financialYearInfo}
                    />
                </section>

                <section className="grid gap-6">
                    <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                        <h2 className="text-lg font-semibold text-slate-900">Team shout-outs</h2>
                        <p className="mt-1 text-sm text-slate-500">Highlight the pros leading conversions, keeping follow-ups warm, and closing full checklists.</p>
                        <div className="mt-4 grid gap-4 sm:grid-cols-3">
                            <TeamLeaderboard title="Top closers" items={teamHighlights.topClosers} highlight="registered" loggedStaffId={loggedStaffId} />
                            <TeamLeaderboard title="Follow-up champions" items={teamHighlights.followupChampions} highlight="pipeline" loggedStaffId={loggedStaffId} />
                            <TeamLeaderboard title="Checklist champions" items={teamHighlights.checklistChampions} highlight="checklistRate" suffix="%" loggedStaffId={loggedStaffId} />
                        </div>
                    </div>
                </section>

                <section className="grid gap-6 lg:grid-cols-3">
                    <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                        <h2 className="text-lg font-semibold text-slate-900">Pipeline snapshot</h2>
                        <p className="mt-1 text-sm text-slate-500">Ensure every lead knows their next step and keep momentum across the funnel.</p>
                        <div className="mt-4 space-y-3">
                            <PipelineRow label="Pending" value={pipelineSnapshot.pending} tone="slate" />
                            <PipelineRow label="In follow-up" value={pipelineSnapshot.followup} tone="sky" />
                            <PipelineRow label="Plan to register" value={pipelineSnapshot.planToRegister} tone="amber" />
                            <PipelineRow label="Stalled (7+ days)" value={pipelineSnapshot.stalled} tone="rose" />
                            <PipelineRow label="Lost" value={pipelineSnapshot.lost} tone="stone" />
                        </div>
                    </div>
                    <LeadStatusSummary items={statusSummary} />
                    <FollowupAttentionPanel attention={followupAttention} onOpen={openFollowupModal} />
                </section>

                <section className="grid gap-6 lg:grid-cols-2">
                    <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                        <h2 className="text-lg font-semibold text-slate-900">Improvement spotlight</h2>
                        <p className="mt-1 text-sm text-slate-500">Share clarity with the team on where sharper focus will unlock the next wave of registrations.</p>
                        <div className="mt-4 space-y-4">
                            {improvementAreas.map((item) => (
                                <div key={item.title} className="rounded-xl border border-slate-100 bg-slate-50 p-4">
                                    <div className="flex flex-wrap items-center justify-between gap-2">
                                        <h3 className="text-sm font-semibold text-slate-800">{item.title}</h3>
                                        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ring-1 ${resolveTrendColor(item.trend)}`}>
                                            <span>{resolveTrendIcon(item.trend)}</span>
                                            <span>{item.change}</span>
                                        </span>
                                    </div>
                                    <p className="mt-2 text-sm text-slate-600">{item.description}</p>
                                    <p className="mt-3 text-xs font-semibold uppercase tracking-wide text-slate-500">Current status</p>
                                    <p className="text-sm font-semibold text-slate-900">{item.metric}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                        <h2 className="text-lg font-semibold text-slate-900">Checklist heatmap</h2>
                        <p className="mt-1 text-sm text-slate-500">Keep registrations audit-ready by tracking which steps need extra love.</p>
                        <div className="mt-4 space-y-3">
                            {checklistGaps.map((gap) => (
                                <div key={gap.key} className="space-y-3 rounded-xl border border-slate-100 bg-white p-4 shadow-sm">
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm font-semibold text-slate-800">{gap.title}</span>
                                        <span className="text-xs font-semibold text-emerald-600">{gap.rate}%</span>
                                    </div>
                                    <div>
                                        <button
                                            type="button"
                                            className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-xs font-semibold text-slate-600 shadow-sm transition hover:border-sky-200 hover:bg-sky-50 hover:text-sky-700 disabled:cursor-not-allowed disabled:opacity-60"
                                            onClick={() => setActiveChecklistGap(gap)}
                                            disabled={!gap.pending.length}
                                        >
                                            View inquiries
                                        </button>
                                    </div>
                                    <div className="flex items-center justify-between text-xs text-slate-500">
                                        <span>{gap.completed} complete</span>
                                        <span>{gap.remaining} remaining</span>
                                    </div>
                                    <div className="relative h-2 w-full overflow-hidden rounded-full bg-slate-200">
                                        <div
                                            className="absolute inset-y-0 left-0 rounded-full bg-emerald-500"
                                            style={{ width: `${Math.min(100, Math.max(0, gap.rate))}%` }}
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            </div>
            )}
            <FollowupAttentionModal
                category={activeFollowupKey}
                group={activeFollowupGroup}
                onClose={closeFollowupModal}
            />
            <ChecklistGapModal gap={activeChecklistGap} onClose={closeChecklistModal} />
        </SalesLayout>
    );
};

const SummaryCard = ({ label, value, tone }) => {
    const toneStyles = {
        neutral: 'bg-slate-900/90 text-white',
        success: 'bg-emerald-500/90 text-white',
        warning: 'bg-amber-500/90 text-white',
    };

    return (
        <div className={`rounded-2xl px-4 py-3 shadow-sm ${toneStyles[tone]}`}>
            <p className="text-[11px] font-semibold uppercase tracking-wide opacity-70">{label}</p>
            <p className="mt-2 text-xl font-semibold">{value}</p>
        </div>
    );
};

const CoursePerformanceHighlights = ({ summary }) => {
    const { strong, weak } = summary;

    if (!strong && !weak) {
        return null;
    }

    const cards = [
        {
            key: 'strong',
            title: 'Strongest course',
            tone: 'border-emerald-200 bg-emerald-50 text-emerald-700',
            value: strong,
        },
        {
            key: 'weak',
            title: 'Needs attention',
            tone: 'border-rose-200 bg-rose-50 text-rose-700',
            value: weak,
        },
    ];

    return (
        <div className="grid gap-3 sm:grid-cols-2">
            {cards.map((card) => {
                const metricTone = card.key === 'strong' ? 'text-emerald-700' : 'text-rose-600';

                return (
                    <div key={card.key} className={`rounded-2xl border p-4 shadow-sm ${card.tone}`}>
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-600">{card.title}</p>
                    {card.value ? (
                        <div className="mt-2">
                                <p className={`text-base font-semibold ${metricTone}`}>{card.value.label}</p>
                                <p className="text-xs font-medium text-slate-600">
                                    {formatIndianNumber(card.value.totalRegistrations)} registrations in 12 months
                                </p>
                        </div>
                        ) : (
                            <p className="mt-2 text-sm text-slate-600">
                                {card.key === 'strong' ? 'No registrations recorded yet.' : 'No course is lagging behind right now.'}
                            </p>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

const CoursePerformanceSection = ({ options, trends, selectedCourseId, onSelect, summary, financialYearInfo }) => {
    const selectOptions = useMemo(
        () => options.map((option) => ({ value: option.id, label: option.label })),
        [options],
    );

    const selectedOption = useMemo(
        () => selectOptions.find((option) => option.value === selectedCourseId) ?? null,
        [selectOptions, selectedCourseId],
    );

    const activeTrend = useMemo(
        () => trends.find((trend) => trend.courseId === selectedCourseId) ?? null,
        [trends, selectedCourseId],
    );

    const chartData = useMemo(() => {
        if (!financialYearInfo) {
            if (!activeTrend) return [];
            return activeTrend.months.map((month) => ({
                label: month.label,
                registeredCount: month.registeredCount,
            }));
        }

        const fyMonths = buildFinancialYearMonths(financialYearInfo);
        const byKey = {};
        if (activeTrend) {
            for (const m of activeTrend.months) {
                byKey[m.monthKey] = m;
            }
        }

        return fyMonths.map(({ monthKey, label }) => {
            const found = byKey[monthKey];
            return {
                label,
                registeredCount: found ? found.registeredCount : 0,
            };
        });
    }, [activeTrend, financialYearInfo]);

    const totalRegistrations = chartData.reduce((accumulator, item) => accumulator + item.registeredCount, 0);

    return (
        <div className="mt-6 space-y-6">
            <CoursePerformanceHighlights summary={summary} />
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                <div className="min-w-[240px] lg:w-80">
                    <Select
                        classNamePrefix="course-select"
                        value={selectedOption}
                        onChange={(option) => onSelect(option ? option.value : null)}
                        options={selectOptions}
                        placeholder="Select a course"
                        isDisabled={!selectOptions.length}
                        noOptionsMessage={() => 'No course data available'}
                        menuPlacement="auto"
                        styles={{
                            control: (base, state) => ({
                                ...base,
                                borderRadius: '0.75rem',
                                borderColor: state.isFocused ? '#38bdf8' : '#cbd5f5',
                                boxShadow: 'none',
                                minHeight: '44px',
                                paddingLeft: '0.25rem',
                                paddingRight: '0.25rem',
                                '&:hover': {
                                    borderColor: '#38bdf8',
                                },
                            }),
                            valueContainer: (base) => ({
                                ...base,
                                padding: '0 0.25rem',
                            }),
                            placeholder: (base) => ({
                                ...base,
                                color: '#94a3b8',
                                fontSize: '0.85rem',
                            }),
                            singleValue: (base) => ({
                                ...base,
                                fontSize: '0.95rem',
                                fontWeight: 600,
                                color: '#0f172a',
                            }),
                            menu: (base) => ({
                                ...base,
                                borderRadius: '0.75rem',
                                overflow: 'hidden',
                            }),
                        }}
                    />
                </div>
                <div className="grid gap-4 text-sm text-slate-600 sm:grid-cols-2 lg:gap-6">
                    <div>
                        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Selected course</p>
                        <p className="mt-1 font-semibold text-slate-900">{selectedOption?.label ?? 'No course selected'}</p>
                    </div>
                    <div>
                        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Registrations</p>
                        <p className="text-[11px] font-medium uppercase tracking-wide text-slate-400">
                            {financialYearInfo
                                ? `${financialYearInfo.shortLabel} (${financialYearInfo.periodLabel})`
                                : `Last ${chartData.length} months`}
                        </p>
                        <p className="mt-1 font-semibold text-emerald-600">{formatIndianNumber(totalRegistrations)}</p>
                    </div>
                </div>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="h-72">
                    {chartData.length ? (
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={chartData} margin={{ top: 16, right: 24, left: 0, bottom: 16 }}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                                <XAxis
                                    dataKey="label"
                                    tickLine={false}
                                    axisLine={{ stroke: '#cbd5f5' }}
                                    tick={{ fontSize: 11, fill: '#475569' }}
                                />
                                <YAxis
                                    allowDecimals={false}
                                    tickLine={false}
                                    axisLine={{ stroke: '#cbd5f5' }}
                                    tick={{ fontSize: 10, fill: '#94a3b8' }}
                                />
                                <Tooltip
                                    formatter={(value) => `${formatIndianNumber(value)} registrations`}
                                    labelFormatter={(label) => label}
                                    cursor={{ fill: 'rgba(148, 163, 184, 0.08)' }}
                                    contentStyle={{ borderRadius: 12, borderColor: '#e2e8f0', fontSize: 12 }}
                                />
                                <Bar
                                    dataKey="registeredCount"
                                    name="Registrations"
                                    fill="#10b981"
                                    radius={[6, 6, 0, 0]}
                                    maxBarSize={36}
                                />
                            </BarChart>
                        </ResponsiveContainer>
                    ) : (
                        <div className="flex h-full items-center justify-center text-sm text-slate-500">
                            Month-wise registrations will appear once this course records sign-ups.
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const TeamLeaderboard = ({ title, items, highlight, suffix = '', loggedStaffId = null }) => (
    <div>
        <h3 className="text-sm font-semibold text-slate-800">{title}</h3>
        {items.length ? (
            <ul className="mt-3 overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-sm">
                {items.map((member, index) => {
                    const isLoggedUser = loggedStaffId !== null && member.id !== null && member.id === loggedStaffId;
                    const baseClasses = 'flex items-center justify-between px-4 py-2.5 text-xs transition';
                    const itemTone = isLoggedUser
                        ? 'bg-sky-50 text-sky-700 ring-1 ring-inset ring-sky-200'
                        : 'text-slate-600 hover:bg-slate-50';

                    return (
                        <li
                            key={`${title}-${member.id ?? member.name}`}
                            className={`${baseClasses} ${itemTone} ${index !== items.length - 1 ? 'border-b border-slate-100' : ''}`}
                        >
                            <div className="flex items-center gap-3">
                                <span className={`flex h-7 w-7 items-center justify-center rounded-full text-[11px] font-semibold ${isLoggedUser ? 'bg-sky-600 text-white' : 'bg-slate-900/80 text-white'}`}>
                                    {index + 1}
                                </span>
                                <p className="text-sm font-semibold text-slate-800">
                                    {member.name}
                                </p>
                            </div>
                            <span className={`text-sm font-semibold ${isLoggedUser ? 'text-sky-700' : 'text-emerald-600'}`}>
                                {member[highlight]}
                                {suffix}
                            </span>
                        </li>
                    );
                })}
            </ul>
        ) : (
            <div className="mt-3 rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-3 py-4 text-center text-xs text-slate-400">
                Data will appear once inquiries are assigned.
            </div>
        )}
    </div>
);

const pipelineToneStyles = {
    slate: 'bg-slate-100 text-slate-700 ring-slate-200',
    sky: 'bg-sky-100 text-sky-700 ring-sky-200',
    amber: 'bg-amber-100 text-amber-700 ring-amber-200',
    rose: 'bg-rose-100 text-rose-700 ring-rose-200',
    stone: 'bg-stone-100 text-stone-700 ring-stone-200',
};

const PipelineRow = ({ label, value, tone }) => (
    <div className={`flex items-center justify-between rounded-xl px-3 py-2 text-sm font-semibold ring-1 ${pipelineToneStyles[tone]}`}>
        <span>{label}</span>
        <span>{value}</span>
    </div>
);

const statusToneStyles = {
    pending: 'bg-slate-100 text-slate-700 ring-slate-200',
    in_followup: 'bg-sky-100 text-sky-700 ring-sky-200',
    plan_to_register: 'bg-amber-100 text-amber-700 ring-amber-200',
    registered: 'bg-emerald-100 text-emerald-700 ring-emerald-200',
    registered_inter_branch: 'bg-cyan-100 text-cyan-700 ring-cyan-200',
    registered_elsewhere: 'bg-indigo-100 text-indigo-700 ring-indigo-200',
    will_not_register: 'bg-rose-100 text-rose-700 ring-rose-200',
};

const LeadStatusSummary = ({ items }) => {
    if (!items.length) {
        return null;
    }

    return (
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-slate-900">Lead status summary</h2>
            <p className="mt-1 text-sm text-slate-500">Understand where inquiries currently sit across the key decision stages.</p>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
                {items.map((item) => {
                    const tone = statusToneStyles[item.key] ?? 'bg-slate-100 text-slate-700 ring-slate-200';

                    return (
                        <div key={item.key} className={`flex items-center justify-between rounded-xl px-3 py-2 text-sm font-semibold ring-1 ${tone}`}>
                            <span>{item.label}</span>
                            <span>{item.value}</span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

const followupCardCopy = {
    missing: {
        title: 'No follow-up scheduled',
        description: 'Set the next reminder for leads waiting on your response.',
        action: 'Review inquiries',
        tone: 'border-amber-200 bg-amber-50 text-amber-700',
        secondaryTone: 'text-amber-600',
    },
    overdue: {
        title: 'Follow-ups overdue',
        description: 'Reconnect with leads whose reminder dates have passed.',
        action: 'Catch up now',
        tone: 'border-rose-200 bg-rose-50 text-rose-700',
        secondaryTone: 'text-rose-600',
    },
};

const FollowupAttentionPanel = ({ attention, onOpen }) => {
    const summaryEntries = [
        { key: 'missing', data: attention.missing, copy: followupCardCopy.missing },
        { key: 'overdue', data: attention.overdue, copy: followupCardCopy.overdue },
    ];

    return (
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-slate-900">Follow-up attention</h2>
            <p className="mt-1 text-sm text-slate-500">Stay ahead on leads that need their next touchpoint scheduled or revived.</p>
            <div className="mt-4 grid gap-4">
                {summaryEntries.map(({ key, data, copy }) => {
                    const hasItems = data.count > 0;

                    return (
                        <div key={key} className={`flex flex-col gap-3 rounded-2xl border p-4 shadow-sm ${copy.tone}`}>
                            <div className="flex flex-col gap-1">
                                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{copy.title}</p>
                                <p className={`text-2xl font-semibold ${copy.secondaryTone}`}>{formatIndianNumber(data.count)}</p>
                                <p className="text-sm text-slate-600">{copy.description}</p>
                            </div>
                            <div>
                                <button
                                    type="button"
                                    className="inline-flex items-center justify-center rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-300"
                                    onClick={() => onOpen(key)}
                                    disabled={!hasItems}
                                >
                                    {copy.action}
                                </button>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

const followupModalCopy = {
    missing: {
        title: 'Leads without a next follow-up date',
        description: 'Assign the next reminder so these leads stay warm.',
        cta: 'Close',
    },
    overdue: {
        title: 'Follow-ups past their reminder date',
        description: 'Reconnect with these leads before momentum fades.',
        cta: 'Dismiss',
    },
};

const FollowupAttentionModal = ({ category, group, onClose }) => {
    if (!category || !group) {
        return null;
    }

    const copy = followupModalCopy[category];

    return (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/60 p-4" role="dialog" aria-modal="true">
            <div className="w-full max-w-4xl overflow-hidden rounded-2xl bg-white shadow-xl">
                <div className="flex items-start justify-between border-b border-slate-100 px-6 py-4">
                    <div>
                        <h3 className="text-lg font-semibold text-slate-900">{copy.title}</h3>
                        <p className="mt-1 text-sm text-slate-500">{copy.description}</p>
                    </div>
                    <button
                        type="button"
                        className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 text-slate-500 transition hover:bg-slate-200 hover:text-slate-700"
                        onClick={onClose}
                        aria-label="Close follow-up modal"
                    >
                        ×
                    </button>
                </div>
                <div className="max-h-[28rem] overflow-y-auto">
                    {group.inquiries.length ? (
                        <table className="min-w-full divide-y divide-slate-200 text-sm">
                            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                                <tr>
                                    <th className="px-4 py-3 text-left">Student</th>
                                    <th className="px-4 py-3 text-left">Status</th>
                                    <th className="px-4 py-3 text-left">Course</th>
                                    <th className="px-4 py-3 text-left">Next follow-up</th>
                                    <th className="px-4 py-3 text-left">Reminder note</th>
                                    <th className="px-4 py-3 text-left">Last comment</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {group.inquiries.map((inquiry) => (
                                    <tr key={inquiry.inquiryId} className="bg-white">
                                        <td className="px-4 py-3">
                                            <div className="font-semibold text-slate-900">{inquiry.studentName}</div>
                                            <div className="text-xs text-slate-500">
                                                {inquiry.contact ?? 'No contact number'}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${resolveStatusBadgeTone(inquiry.statusKey)}`}>
                                                {inquiry.statusLabel}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-sm text-slate-600">{inquiry.course ?? '—'}</td>
                                        <td className="px-4 py-3 text-sm text-slate-600">
                                            <div>{formatDisplayDate(inquiry.nextReminderDate)}</div>
                                            <div className="text-xs text-slate-400">Last touch: {formatDisplayDate(inquiry.lastFollowupDate)}</div>
                                        </td>
                                        <td className="px-4 py-3 text-sm text-slate-600">
                                            {inquiry.nextReminderComment ? (
                                                <p className="max-w-xs whitespace-pre-wrap break-words">{inquiry.nextReminderComment}</p>
                                            ) : (
                                                <p className="text-xs text-slate-400">No reminder note captured</p>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 text-sm text-slate-600">
                                            {inquiry.lastComment ? (
                                                <p className="max-w-xs whitespace-pre-wrap break-words">{inquiry.lastComment}</p>
                                            ) : (
                                                <p className="text-xs text-slate-400">No follow-up comment</p>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : (
                        <div className="flex h-48 items-center justify-center text-sm text-slate-500">
                            Nothing to action here right now.
                        </div>
                    )}
                </div>
                <div className="flex items-center justify-end gap-3 border-t border-slate-100 bg-slate-50 px-6 py-4">
                    <button
                        type="button"
                        className="inline-flex items-center justify-center rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800"
                        onClick={onClose}
                    >
                        {copy.cta}
                    </button>
                </div>
            </div>
        </div>
    );
};

const ChecklistGapModal = ({ gap, onClose }) => {
    const [query, setQuery] = useState('');
    const hasPending = !!(gap && Array.isArray(gap.pending) && gap.pending.length > 0);

    const filteredPending = useMemo(() => {
        if (!hasPending || !gap || !Array.isArray(gap.pending)) {
            return [];
        }

        const normalized = query.trim().toLowerCase();
        if (!normalized) {
            return gap.pending;
        }

        return gap.pending.filter((item) => {
            const haystack = [
                item.studentName,
                item.assignedTo ?? '',
                item.course ?? '',
                item.contact ?? '',
                item.statusLabel ?? '',
                String(item.inquiryId ?? ''),
            ]
                .join(' ')
                .toLowerCase();

            return haystack.includes(normalized);
        });
    }, [gap, hasPending, query]);

    if (!gap) {
        return null;
    }

    return (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/60 p-4" role="dialog" aria-modal="true">
            <div className="w-full max-w-4xl overflow-hidden rounded-2xl bg-white shadow-xl">
                <div className="flex items-start justify-between border-b border-slate-100 px-6 py-4">
                    <div>
                        <h3 className="text-lg font-semibold text-slate-900">{gap.title}</h3>
                        <p className="mt-1 text-sm text-slate-500">
                            {hasPending
                                ? `Review ${filteredPending.length} of ${gap.pending.length} registrations missing this checklist step.`
                                : 'Every eligible registration has this checklist step completed.'}
                        </p>
                    </div>
                    <button
                        type="button"
                        className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 text-slate-500 transition hover:bg-slate-200 hover:text-slate-700"
                        onClick={onClose}
                        aria-label="Close checklist gap modal"
                    >
                        ×
                    </button>
                </div>
                <div className="max-h-[28rem] overflow-y-auto">
                    {hasPending ? (
                        <div className="px-6 pb-4">
                            <div className="mb-4 flex items-center gap-2">
                                <input
                                    type="search"
                                    value={query}
                                    onChange={(event) => setQuery(event.target.value)}
                                    className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm placeholder:text-slate-400 focus:border-sky-300 focus:outline-none focus:ring-2 focus:ring-sky-200"
                                    placeholder="Search by student, course, staff, contact, or status"
                                />
                                {query && (
                                    <button
                                        type="button"
                                        className="inline-flex items-center justify-center rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-xs font-semibold text-slate-600 shadow-sm transition hover:border-slate-300 hover:text-slate-800"
                                        onClick={() => setQuery('')}
                                    >
                                        Clear
                                    </button>
                                )}
                            </div>
                            {filteredPending.length ? (
                                <table className="min-w-full divide-y divide-slate-200 text-sm">
                                    <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                                        <tr>
                                            <th className="px-4 py-3 text-left">Student</th>
                                            <th className="px-4 py-3 text-left">Status</th>
                                            <th className="px-4 py-3 text-left">Assigned to</th>
                                            <th className="px-4 py-3 text-left">Course</th>
                                            <th className="px-4 py-3 text-left">Contact</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100">
                                        {filteredPending.map((inquiry) => (
                                            <tr key={inquiry.inquiryId} className="bg-white">
                                                <td className="px-4 py-3">
                                                    <div className="font-semibold text-slate-900">{inquiry.studentName}</div>
                                                    <div className="text-xs text-slate-400">Inquiry #{inquiry.inquiryId}</div>
                                                </td>
                                                <td className="px-4 py-3">
                                                    <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${resolveStatusBadgeTone(inquiry.status)}`}>
                                                        {inquiry.statusLabel}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3 text-sm text-slate-600">{inquiry.assignedTo ?? 'Unassigned'}</td>
                                                <td className="px-4 py-3 text-sm text-slate-600">{inquiry.course ?? '—'}</td>
                                                <td className="px-4 py-3 text-sm text-slate-600">{inquiry.contact ?? 'No contact provided'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            ) : (
                                <div className="flex h-40 items-center justify-center rounded-xl border border-dashed border-slate-200 bg-slate-50 text-sm text-slate-500">
                                    No inquiries match your search.
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="flex h-48 items-center justify-center text-sm text-slate-500">
                            Nothing pending for this checklist step. 🎉
                        </div>
                    )}
                </div>
                <div className="flex items-center justify-end gap-3 border-t border-slate-100 bg-slate-50 px-6 py-4">
                    <button
                        type="button"
                        className="inline-flex items-center justify-center rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800"
                        onClick={onClose}
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

function formatDisplayDate(value) {
    if (!value) {
        return '—';
    }

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
        return value;
    }

    return new Intl.DateTimeFormat('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
    }).format(parsed);
}

function resolveStatusBadgeTone(statusKey) {
    const normalized = statusKey.toLowerCase();

    if (normalized.includes('plan')) {
        return 'bg-sky-100 text-sky-700';
    }

    if (normalized.includes('follow')) {
        return 'bg-amber-100 text-amber-700';
    }

    if (normalized.includes('register')) {
        return 'bg-emerald-100 text-emerald-700';
    }

    return 'bg-slate-100 text-slate-700';
}

function formatAmount(value) {
    const amount = Number.isFinite(value) ? (value ?? 0) : 0;
    return `Rs ${new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(Math.round(amount))}`;
}

function formatPercent(value) {
    const percent = Number.isFinite(value) ? (value ?? 0) : 0;
    const nonNegative = Math.max(0, percent);
    const formatted = new Intl.NumberFormat('en-US', { maximumFractionDigits: nonNegative >= 10 ? 0 : 1 }).format(nonNegative);
    return `${formatted}%`;
}

function resolveAchievementTone(rate) {
    if (rate >= 110) {
        return 'text-emerald-600';
    }

    if (rate >= 90) {
        return 'text-emerald-500';
    }

    if (rate >= 70) {
        return 'text-amber-500';
    }

    return 'text-rose-600';
}

function buildMotivationSnippet(targetAmount, registeredAmount, balanceAmount) {
    const progress = targetAmount > 0 ? (registeredAmount / targetAmount) * 100 : 0;
    const rounded = Math.round(progress);

    if (rounded >= 100) {
        return 'Target crushed—time to pre-book next month\'s wins.';
    }

    if (rounded >= 75) {
        return `Only ${formatAmount(balanceAmount)} left—double down on high-intent leads.`;
    }

    if (rounded >= 50) {
        return 'Momentum is building—prioritise planned registrations to finish strong.';
    }

    return 'Re-ignite dormant follow-ups to jump-start this month\'s run.';
}

function MonthlyRegistrationChart({ data, financialYearInfo }) {
    if (!data.length) {
        return (
            <div className="mt-6 space-y-2">
                {financialYearInfo ? (
                    <p className="text-xs font-medium uppercase tracking-wide text-slate-400">
                        Financial year {financialYearInfo.shortLabel} ({financialYearInfo.periodLabel})
                    </p>
                ) : null}
                <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 p-6 text-center text-sm text-slate-500">
                    Monthly performance will appear once targets and registrations are recorded.
                </div>
            </div>
        );
    }
    const chartData = (() => {
        if (!financialYearInfo) {
            return data.map((item) => {
                const targetAmount = item.targetAmount ?? 0;
                const achievedAmount = item.registeredAmount ?? 0;
                const providedRate = typeof item.achievementRate === 'number' ? item.achievementRate : null;
                const derivedRate = targetAmount > 0 ? (achievedAmount / targetAmount) * 100 : 0;
                const achievementRate = providedRate !== null && Number.isFinite(providedRate) ? providedRate : derivedRate;

                return {
                    monthKey: item.monthKey,
                    label: item.label,
                    targetAmount,
                    achievedAmount,
                    achievementRate,
                };
            });
        }

        const fyMonths = buildFinancialYearMonths(financialYearInfo);
        const byKey = {};
        for (const row of data) {
            byKey[row.monthKey] = row;
        }

        return fyMonths.map(({ monthKey, label }) => {
            const item = byKey[monthKey];
            const targetAmount = item?.targetAmount ?? 0;
            const achievedAmount = item?.registeredAmount ?? 0;
            const providedRate = typeof item?.achievementRate === 'number' ? item.achievementRate : null;
            const derivedRate = targetAmount > 0 ? (achievedAmount / targetAmount) * 100 : 0;
            const achievementRate = providedRate !== null && Number.isFinite(providedRate) ? (providedRate) : derivedRate;

            return {
                monthKey,
                label,
                targetAmount,
                achievedAmount,
                achievementRate,
            };
        });
    })();

    const totals = chartData.reduce(
        (acc, row) => {
            acc.target += row.targetAmount ?? 0;
            acc.achieved += row.achievedAmount ?? 0;
            return acc;
        },
        { target: 0, achieved: 0 },
    );
    const pendingTotal = Math.max(0, totals.target - totals.achieved);
    const overallRate = totals.target > 0 ? (totals.achieved / totals.target) * 100 : 0;
    const overallRateRounded = Math.round(overallRate);

    return (
        <div className="mt-6 space-y-3">
            {financialYearInfo ? (
                <p className="text-xs font-medium uppercase tracking-wide text-slate-400">
                    Financial year {financialYearInfo.shortLabel} ({financialYearInfo.periodLabel})
                </p>
            ) : null}
            <div className="h-80 w-full">
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                        <XAxis
                            dataKey="label"
                            tickLine={false}
                            axisLine={{ stroke: '#cbd5f5' }}
                            tick={{ fontSize: 11, fill: '#475569' }}
                        />
                        <YAxis
                            tickLine={false}
                            axisLine={{ stroke: '#cbd5f5' }}
                            tickFormatter={(value) => new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(value)}
                            tick={{ fontSize: 10, fill: '#94a3b8' }}
                        />
                        <Tooltip
                            formatter={(value) => formatAmount(value)}
                            labelFormatter={(label) => label}
                            cursor={{ fill: 'rgba(148, 163, 184, 0.1)' }}
                            contentStyle={{ borderRadius: 12, borderColor: '#e2e8f0', fontSize: 12 }}
                        />
                        <Legend
                            verticalAlign="top"
                            align="right"
                            wrapperStyle={{ fontSize: 11, color: '#64748b' }}
                        />
                        <Bar
                            dataKey="targetAmount"
                            name="Target amount"
                            fill="#cbd5f5"
                            radius={[4, 4, 0, 0]}
                            maxBarSize={28}
                        />
                        <Bar
                            dataKey="achievedAmount"
                            name="Achieved amount"
                            fill="#22c55e"
                            radius={[4, 4, 0, 0]}
                            maxBarSize={28}
                        >
                            <LabelList
                                dataKey="achievementRate"
                                position="top"
                                fill="#15803d"
                                formatter={(value) => (typeof value === 'number' ? formatPercent(value) : '')}
                            />
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </div>
            <div className="grid gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:grid-cols-3">
                <div className="rounded-xl bg-slate-50 px-3 py-2">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Total target</p>
                    <p className="mt-1 text-sm font-semibold text-slate-900">{formatAmount(totals.target)}</p>
                </div>
                <div className="rounded-xl bg-emerald-50 px-3 py-2">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-700">Total achieved</p>
                    <p className="mt-1 text-sm font-semibold text-emerald-700">{formatAmount(totals.achieved)}</p>
                </div>
                <div className="rounded-xl bg-amber-50 px-3 py-2">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-amber-700">Pending amount</p>
                    <p className="mt-1 text-sm font-semibold text-amber-700">{formatAmount(pendingTotal)}</p>
                </div>
            </div>
            <div className="mt-3 space-y-2">
                <div className="flex items-center justify-between">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Overall achievement</p>
                    <p className={`text-sm font-semibold ${resolveAchievementTone(overallRateRounded)}`}>{overallRateRounded}%</p>
                </div>
                <div className="relative h-2 w-full overflow-hidden rounded-full bg-slate-200">
                    <div
                        className="absolute inset-y-0 left-0 rounded-full bg-emerald-500"
                        style={{ width: `${Math.min(100, Math.max(0, overallRate))}%` }}
                    />
                </div>
            </div>
        </div>
    );
}

export default Performance;
